import { useEffect, useRef, useState } from 'react';
import { CATEGORIES } from '../data/questions';
import type { Category } from '../types';

type GradientStop = { pos: number; color: readonly [number, number, number] };

interface CategoryCopy {
  displayLabel?: string;
  subtitle: string;
}

interface SpectrumNode {
  id: Category;
  fullLabel: string;
  displayLabel: string;
  emoji: string;
  subtitle: string;
  y: number;
  color: string;
}

interface PoleMarkerProps {
  emoji: string;
  label: string;
  emojiFirst?: boolean;
  labelColor?: string;
}

export interface SpectrumVisualState {
  mercuryBottom: number;
  mercuryHeight: number;
  hiddenMercuryHeight: number;
  glowHeight: number;
  lastActiveIndex: number;
}

const CATEGORY_COPY: Record<Category, CategoryCopy> = {
  basics: { subtitle: 'Romance & Foreplay' },
  positions: { subtitle: 'Mix It Up' },
  'body-preferences': {
    displayLabel: 'Body Prefs',
    subtitle: 'Touch & Grooming',
  },
  roleplay: { subtitle: "Let's Pretend" },
  'frequency-location': {
    displayLabel: 'When & Where',
    subtitle: 'Public & Private',
  },
  'toys-devices': { subtitle: 'Bring a Friend' },
  'butt-stuff': { subtitle: 'Anal Play' },
  bdsm: { subtitle: 'Power & Control' },
  swingers: { subtitle: 'More the Merrier' },
};

const GRADIENT_STOPS: readonly GradientStop[] = [
  { pos: 0, color: [180, 120, 220] },    // soft purple
  { pos: 0.5, color: [255, 51, 102] },   // #FF3366 hot pink (brand primary)
  { pos: 1, color: [255, 69, 0] },       // #FF4500 fiery orange-red
];

const THERMOMETER_GRADIENT =
  'linear-gradient(to bottom, rgb(180, 120, 220), rgb(255, 51, 102), rgb(255, 69, 0))';
const STEM_BG = 'rgba(0, 0, 0, 0.45)';
const NODE_COUNT = CATEGORIES.length;
const ROW_H = 68;
const MOBILE_PILL_W = 104;
const DESKTOP_PILL_W = 148;
const PILL_POLE_H = 104;
const STEM_OVERSHOOT = 24;
const FIRST_NODE_Y = PILL_POLE_H;
const TRACK_H = (NODE_COUNT - 1) * ROW_H;
const LAST_NODE_Y = FIRST_NODE_Y + TRACK_H;
const MERCURY_TRAVEL = LAST_NODE_Y - FIRST_NODE_Y;
const PILL_H = LAST_NODE_Y + PILL_POLE_H;
const STEM_W = 28;
const INACTIVE_OPACITY = 0.48;
const TICK_W = 12;
const GLOW_MIN_HEIGHT = 36;
const PILL_WIDTH_CSS_VAR = 'var(--spectrum-pill-width)';
const PILL_RADIUS_CSS_VAR = 'calc(var(--spectrum-pill-width) / 2)';
const LABEL_WIDTH_CSS_VAR = 'var(--spectrum-label-width)';
const GAUGE_TRACK_TOP = FIRST_NODE_Y - STEM_OVERSHOOT;
const FULL_MERCURY_HEIGHT = MERCURY_TRAVEL + STEM_OVERSHOOT * 2;
const STEM_LEFT = `calc((${PILL_WIDTH_CSS_VAR} - ${STEM_W}px) / 2)`;
const GLOW_LEFT = `calc((${PILL_WIDTH_CSS_VAR} - ${STEM_W + 4}px) / 2)`;
const WATERMARK_LEFT = `calc((${PILL_WIDTH_CSS_VAR} - ${STEM_W}px) / 4)`;
const TICK_RIGHT = `calc((${PILL_WIDTH_CSS_VAR} - ${STEM_W}px - ${TICK_W * 2}px) / 4)`;
const SCROLL_PROGRESS_START = 0.25;
const SCROLL_PROGRESS_RANGE = 0.4;

export const SPICE_SPECTRUM_LAYOUT = {
  rowHeight: ROW_H,
  firstNodeY: FIRST_NODE_Y,
  lastNodeY: LAST_NODE_Y,
  pillWidth: DESKTOP_PILL_W,
  mobilePillWidth: MOBILE_PILL_W,
  desktopPillWidth: DESKTOP_PILL_W,
  pillHeight: PILL_H,
  mercuryTravel: MERCURY_TRAVEL,
};

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function interpolateColor(t: number): string {
  const clamped = clamp01(t);

  let lower = GRADIENT_STOPS[0]!;
  let upper = GRADIENT_STOPS[GRADIENT_STOPS.length - 1]!;

  for (let i = 0; i < GRADIENT_STOPS.length - 1; i++) {
    const current = GRADIENT_STOPS[i]!;
    const next = GRADIENT_STOPS[i + 1]!;
    if (clamped >= current.pos && clamped <= next.pos) {
      lower = current;
      upper = next;
      break;
    }
  }

  const range = upper.pos - lower.pos;
  const local = range === 0 ? 0 : (clamped - lower.pos) / range;

  const r = Math.round(lower.color[0] + (upper.color[0] - lower.color[0]) * local);
  const g = Math.round(lower.color[1] + (upper.color[1] - lower.color[1]) * local);
  const b = Math.round(lower.color[2] + (upper.color[2] - lower.color[2]) * local);

  return `rgb(${r}, ${g}, ${b})`;
}

function buildSpectrumNodes(): SpectrumNode[] {
  return CATEGORIES.map((category, index) => {
    const copy = CATEGORY_COPY[category.id];
    const y = FIRST_NODE_Y + index * ROW_H;

    return {
      id: category.id,
      fullLabel: category.label,
      displayLabel: copy.displayLabel ?? category.label,
      emoji: category.emoji,
      subtitle: copy.subtitle,
      y,
      color: getTrackColorAtY(y),
    };
  });
}

function getLastActiveIndex(mercuryBottom: number, nodes: readonly SpectrumNode[]): number {
  for (let index = nodes.length - 1; index >= 0; index--) {
    if (mercuryBottom >= nodes[index]!.y) {
      return index;
    }
  }

  return -1;
}

const NODES = buildSpectrumNodes();

export function getTrackColorAtY(y: number): string {
  return interpolateColor((y - FIRST_NODE_Y) / MERCURY_TRAVEL);
}

export function getSpectrumVisualState(progress: number): SpectrumVisualState {
  const mercuryBottom = FIRST_NODE_Y + clamp01(progress) * MERCURY_TRAVEL;
  const mercuryHeight = Math.max(
    STEM_W,
    mercuryBottom - FIRST_NODE_Y + STEM_OVERSHOOT + clamp01(progress) * STEM_OVERSHOOT,
  );
  const hiddenMercuryHeight = Math.max(0, FULL_MERCURY_HEIGHT - mercuryHeight);
  const glowHeight = mercuryHeight > 0 ? Math.max(mercuryHeight, GLOW_MIN_HEIGHT) : 0;

  return {
    mercuryBottom,
    mercuryHeight,
    hiddenMercuryHeight,
    glowHeight,
    lastActiveIndex: getLastActiveIndex(mercuryBottom, NODES),
  };
}

export function getSpectrumScrollProgress(
  sectionTop: number,
  sectionHeight: number,
  viewportHeight: number,
): number {
  const raw = (viewportHeight - sectionTop) / (viewportHeight + sectionHeight);
  return clamp01((raw - SCROLL_PROGRESS_START) / SCROLL_PROGRESS_RANGE);
}

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReduced(mq.matches);
    sync();

    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', sync);
      return () => mq.removeEventListener('change', sync);
    }

    mq.addListener(sync);
    return () => mq.removeListener(sync);
  }, []);

  return reduced;
}

function useScrollProgress(sectionRef: React.RefObject<HTMLElement | null>): number {
  const rafRef = useRef(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const syncProgress = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const section = sectionRef.current;
        if (!section) return;

        const rect = section.getBoundingClientRect();
        const nextProgress = getSpectrumScrollProgress(
          rect.top,
          rect.height,
          window.innerHeight,
        );

        setProgress((current) => (current === nextProgress ? current : nextProgress));
      });
    };

    window.addEventListener('scroll', syncProgress, { passive: true });
    window.addEventListener('resize', syncProgress);
    syncProgress();

    return () => {
      window.removeEventListener('scroll', syncProgress);
      window.removeEventListener('resize', syncProgress);
      cancelAnimationFrame(rafRef.current);
    };
  }, [sectionRef]);

  return progress;
}

function PoleMarker({
  emoji,
  label,
  emojiFirst = true,
  labelColor = 'rgba(255, 255, 255, 0.88)',
}: PoleMarkerProps) {
  const emojiNode = (
    <span
      aria-hidden="true"
      style={{
        fontSize: 25,
        lineHeight: 1,
        filter: 'drop-shadow(0 6px 10px rgba(0,0,0,0.35))',
      }}
    >
      {emoji}
    </span>
  );

  const labelNode = (
    <span
      className="font-extrabold uppercase tracking-[0.28em]"
      style={{
        fontSize: 12,
        lineHeight: 1.05,
        color: labelColor,
        textShadow: '0 1px 8px rgba(0,0,0,0.35)',
      }}
    >
      {label}
    </span>
  );

  return (
    <div
      className="flex min-h-[52px] flex-col items-center justify-center gap-1.5 text-center text-white"
    >
      {emojiFirst ? emojiNode : labelNode}
      {emojiFirst ? labelNode : emojiNode}
    </div>
  );
}

function PillGauge({
  progress,
  reducedMotion,
}: {
  progress: number;
  reducedMotion: boolean;
}) {
  const { mercuryBottom, mercuryHeight, hiddenMercuryHeight, glowHeight } =
    getSpectrumVisualState(progress);
  const glowVisible = mercuryHeight > 0;

  return (
    <div
      className="relative flex-shrink-0"
      style={{
        width: PILL_WIDTH_CSS_VAR,
        height: PILL_H,
        borderRadius: PILL_RADIUS_CSS_VAR,
        background: 'rgba(255, 255, 255, 0.03)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow:
          'inset 0 0 40px rgba(255,255,255,0.02), 0 24px 56px rgba(0,0,0,0.55)',
        backdropFilter: 'blur(12px)',
      }}
    >
      <div
        className="pointer-events-none absolute left-0 right-0 top-[18px] flex select-none flex-col items-center"
        data-spectrum-marker="sweet"
      >
        <PoleMarker emoji="🍦" label="Sweet" labelColor="rgba(180, 120, 220, 0.95)" />
      </div>

      <div
        className="pointer-events-none absolute bottom-[18px] left-0 right-0 flex select-none flex-col items-center"
        data-spectrum-marker="spicy"
      >
        <PoleMarker
          emoji="🌶️"
          label="Spicy"
          emojiFirst={false}
          labelColor="rgba(255, 69, 0, 0.95)"
        />
      </div>

      <div
        className="pointer-events-none absolute flex select-none items-center justify-center font-extrabold uppercase"
        style={{
          left: WATERMARK_LEFT,
          top: FIRST_NODE_Y,
          height: MERCURY_TRAVEL,
          writingMode: 'vertical-rl',
          transform: 'rotate(180deg) translateX(50%)',
          fontSize: 13,
          letterSpacing: '0.38em',
          color: 'rgba(255, 255, 255, 0.2)',
        }}
      >
        Spice-o-meter
      </div>

      <div
        className="absolute rounded-full"
        style={{
          left: STEM_LEFT,
          top: GAUGE_TRACK_TOP,
          width: STEM_W,
          height: FULL_MERCURY_HEIGHT,
          backgroundColor: STEM_BG,
          border: '1px solid rgba(255, 255, 255, 0.06)',
        }}
      />

      <div
        className="pointer-events-none absolute rounded-full"
        data-spectrum-mercury-glow="true"
        style={{
          left: GLOW_LEFT,
          top: GAUGE_TRACK_TOP,
          width: STEM_W + 4,
          height: glowHeight,
          opacity: glowVisible ? 0.6 : 0,
          background: THERMOMETER_GRADIENT,
          backgroundSize: `100% ${FULL_MERCURY_HEIGHT}px`,
          filter: reducedMotion ? 'blur(5px)' : `blur(${7 + progress * 6}px)`,
          boxShadow:
            glowVisible
              ? reducedMotion
                ? '0 0 10px rgba(255, 102, 136, 0.14)'
                : `0 0 ${12 + progress * 11}px rgba(255, 51, 102, ${0.1 + progress * 0.12}), 0 0 ${20 + progress * 20}px rgba(255, 69, 0, ${0.06 + progress * 0.1})`
              : 'none',
        }}
      />

      <div
        className="absolute rounded-full"
        style={{
          left: STEM_LEFT,
          top: GAUGE_TRACK_TOP,
          width: STEM_W,
          height: FULL_MERCURY_HEIGHT,
          background: THERMOMETER_GRADIENT,
          backgroundSize: `100% ${FULL_MERCURY_HEIGHT}px`,
          boxShadow:
            progress > 0
              ? reducedMotion
                ? '0 0 10px rgba(255, 69, 0, 0.2)'
                : `0 0 ${16 + progress * 8}px rgba(255, 69, 0, ${0.15 + progress * 0.3}), 0 0 ${32 + progress * 16}px rgba(255, 51, 102, ${progress * 0.15})`
              : 'none',
          clipPath: `inset(0 0 ${hiddenMercuryHeight}px 0 round 999px)`,
          willChange: 'clip-path',
        }}
      />

      {/* Flame inside the mercury, at the trailing (bottom) edge */}
      {mercuryHeight > 0 && (
        <div
          data-spectrum-flame="true"
          className="pointer-events-none absolute select-none"
          style={{
            left: '50%',
            top: GAUGE_TRACK_TOP + mercuryHeight - (17 + progress * 11) * 1.1,
            transform: `translate(-50%, 0)`,
            fontSize: 17 + progress * 11,
            lineHeight: 1,
            filter: reducedMotion
              ? 'none'
              : `drop-shadow(0 0 ${4 + progress * 8}px rgba(255, 69, 0, ${0.3 + progress * 0.4}))`,
            transition: reducedMotion ? 'none' : 'filter 0.3s ease',
            zIndex: 2,
          }}
        >
          🔥
        </div>
      )}

      {NODES.map((node) => {
        const activated = mercuryBottom >= node.y;

        return (
          <div
            key={`pill-tick-${node.id}`}
            style={{
              position: 'absolute',
              top: node.y,
              right: TICK_RIGHT,
              width: TICK_W,
              height: 2,
              borderRadius: 1,
              transform: 'translateY(-50%)',
              backgroundColor: activated ? node.color : 'rgba(255,255,255,0.18)',
              boxShadow: activated ? `0 0 8px ${node.color}88, 0 0 14px ${node.color}40` : 'none',
              transition: 'background-color 0.25s ease, box-shadow 0.25s ease',
            }}
          />
        );
      })}
    </div>
  );
}

function CategoryLabels({ mercuryBottom }: { mercuryBottom: number }) {
  const lastActiveIndex = getLastActiveIndex(mercuryBottom, NODES);

  return (
    <ol
      className="relative m-0 max-w-full min-w-0 list-none p-0"
      style={{ width: LABEL_WIDTH_CSS_VAR, height: PILL_H }}
    >
      {NODES.map((node, index) => {
        const activated = mercuryBottom >= node.y;
        const isLastActive = index === lastActiveIndex;
        const usesCompactLabel = node.displayLabel !== node.fullLabel;

        return (
          <li
            key={node.id}
            className="absolute left-0 right-0"
            style={{
              top: node.y,
              transform: isLastActive
                ? 'translateY(-50%) scale(1.08)'
                : 'translateY(-50%) scale(1)',
              transformOrigin: 'left center',
              transition: 'transform 0.3s ease, opacity 0.25s ease',
            }}
          >
            <div
              className="flex items-start gap-3 md:gap-3.5"
              style={{ opacity: activated ? 1 : INACTIVE_OPACITY }}
            >
              <span
                aria-hidden="true"
                className="text-2xl leading-none md:text-3xl shrink-0"
                style={{
                  filter: activated ? `drop-shadow(0 0 10px ${node.color}55)` : 'none',
                  transition: 'filter 0.25s ease',
                }}
              >
                {node.emoji}
              </span>

              <span className="min-w-0 flex flex-col items-start text-left leading-tight">
                <span className="break-words text-base font-bold text-white md:text-lg">
                  {usesCompactLabel ? (
                    <>
                      <span className="sr-only">{node.fullLabel}</span>
                      <span aria-hidden="true">{node.displayLabel}</span>
                    </>
                  ) : (
                    node.fullLabel
                  )}
                </span>
                <span className="mt-0.5 block break-words text-xs text-white/45 md:text-sm">
                  {node.subtitle}
                </span>
              </span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export default function SpiceSpectrum() {
  const sectionRef = useRef<HTMLElement>(null);
  const reducedMotion = useReducedMotion();
  const progress = useScrollProgress(sectionRef);
  const { mercuryBottom } = getSpectrumVisualState(progress);

  return (
    <section ref={sectionRef} className="mb-24">
      <div className="mb-10 text-center">
        <h2 className="mb-3 text-2xl font-extrabold text-balance md:text-3xl">
          From vanilla to wild
        </h2>
        <p className="text-sm text-slate-400">
          150+ questions across {CATEGORIES.length} categories
        </p>
      </div>

      <div className="px-4">
        <div className="relative mx-auto grid w-fit max-w-full grid-cols-[auto_minmax(0,1fr)] items-start gap-x-4 [--spectrum-label-width:15rem] [--spectrum-pill-width:104px] md:gap-x-6 md:[--spectrum-label-width:16rem] md:[--spectrum-pill-width:148px]">
          <div
            aria-hidden="true"
            className="relative shrink-0"
            data-spectrum-gauge="true"
            style={{ width: PILL_WIDTH_CSS_VAR, height: PILL_H }}
          >
            <PillGauge progress={progress} reducedMotion={reducedMotion} />
          </div>

          <CategoryLabels mercuryBottom={mercuryBottom} />
        </div>
      </div>
    </section>
  );
}
